// Formas do quadro (0.65): linha reta, seta, retângulo, círculo, triângulo e estrela, e o "ajeitar a forma":
// desenhou à mão e segurou o dedo no fim do traço, o app troca pela forma perfeita.
export type P = [number, number];
export type Forma = 'linha' | 'seta' | 'retangulo' | 'circulo' | 'triangulo' | 'estrela';

const r1 = (n: number) => Math.round(n * 10) / 10;
const caminho = (pts: P[], fechar = false) => pts.map(([x, y], i) => `${i ? 'L' : 'M'}${r1(x)} ${r1(y)}`).join('') + (fechar ? `L${r1(pts[0][0])} ${r1(pts[0][1])}` : '');

/** Caminho SVG da forma dentro da caixa (x, y, w, h). Linha e seta vão de (x, y) até (x + w, y + h). */
export function caminhoForma(f: Forma, x: number, y: number, w: number, h: number): string {
  switch (f) {
    case 'linha': return caminho([[x, y], [x + w, y + h]]);
    case 'seta': {
      const ang = Math.atan2(h, w), L = Math.max(18, Math.min(40, Math.hypot(w, h) * 0.25)), a = Math.PI / 7;
      const fx = x + w, fy = y + h;
      return caminho([[x, y], [fx, fy]]) + caminho([[fx - L * Math.cos(ang - a), fy - L * Math.sin(ang - a)], [fx, fy], [fx - L * Math.cos(ang + a), fy - L * Math.sin(ang + a)]]);
    }
    case 'retangulo': return caminho([[x, y], [x + w, y], [x + w, y + h], [x, y + h]], true);
    case 'triangulo': return caminho([[x + w / 2, y], [x + w, y + h], [x, y + h]], true);
    case 'circulo': {
      const cx = x + w / 2, cy = y + h / 2, pts: P[] = [];
      for (let i = 0; i < 48; i++) { const a = (i / 48) * Math.PI * 2; pts.push([cx + (Math.cos(a) * w) / 2, cy + (Math.sin(a) * h) / 2]); }
      return caminho(pts, true);
    }
    case 'estrela': {
      const cx = x + w / 2, cy = y + h / 2, pts: P[] = [];
      for (let i = 0; i < 10; i++) { const a = (Math.PI / 5) * i - Math.PI / 2, k = i % 2 ? 0.42 : 1; pts.push([cx + (Math.cos(a) * w * k) / 2, cy + (Math.sin(a) * h * k) / 2]); }
      return caminho(pts, true);
    }
  }
}

const dist = (a: P, b: P) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const compr = (pts: P[]) => pts.reduce((s, p, i) => (i ? s + dist(p, pts[i - 1]) : 0), 0);

/** Simplifica o traço (Douglas-Peucker) para achar os cantos. */
function simplificar(pts: P[], eps: number): P[] {
  if (pts.length < 3) return pts;
  const [a, b] = [pts[0], pts[pts.length - 1]];
  let idx = 0, max = 0;
  for (let i = 1; i < pts.length - 1; i++) {
    const p = pts[i];
    const d = Math.abs((b[1] - a[1]) * p[0] - (b[0] - a[0]) * p[1] + b[0] * a[1] - b[1] * a[0]) / (dist(a, b) || 1);
    if (d > max) { max = d; idx = i; }
  }
  if (max <= eps) return [a, b];
  return [...simplificar(pts.slice(0, idx + 1), eps).slice(0, -1), ...simplificar(pts.slice(idx), eps)];
}

/** Reconhece a forma desenhada à mão e devolve o caminho perfeito, ou null se não parecer nenhuma forma. */
export function ajeitarForma(pts: P[]): string | null {
  if (pts.length < 4) return null;
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  const x0 = Math.min(...xs), y0 = Math.min(...ys), w = Math.max(...xs) - x0, h = Math.max(...ys) - y0;
  const diag = Math.hypot(w, h);
  if (diag < 30) return null;
  const L = compr(pts);
  const ini = pts[0], fim = pts[pts.length - 1];
  const fechado = dist(ini, fim) < Math.max(25, diag * 0.22);
  if (!fechado) {
    // quase reto: vira linha
    if (dist(ini, fim) / L > 0.88) return caminho([ini, fim]);
    return null;
  }
  // círculo: distância ao centro quase igual em toda a volta
  const cx = x0 + w / 2, cy = y0 + h / 2;
  const raios = pts.map(p => Math.hypot((p[0] - cx) / (w / 2 || 1), (p[1] - cy) / (h / 2 || 1)));
  const media = raios.reduce((s, r) => s + r, 0) / raios.length;
  const desvio = Math.sqrt(raios.reduce((s, r) => s + (r - media) ** 2, 0) / raios.length);
  const cantos = simplificar([...pts, ini], diag * 0.09);
  const nCantos = Math.max(0, cantos.length - 1);
  if (desvio / media < 0.13 && nCantos > 4) return caminhoForma('circulo', x0, y0, w, h);
  if (nCantos === 3) return caminho(cantos.slice(0, 3), true);
  if (nCantos === 4) {
    // quase alinhado com a tela: retângulo certinho; torto: os 4 cantos ligados por retas
    const alinhado = cantos.slice(0, 4).every((p, i, a) => { const q = a[(i + 1) % 4]; const ang = Math.abs(Math.atan2(q[1] - p[1], q[0] - p[0])) % (Math.PI / 2); return ang < 0.25 || ang > Math.PI / 2 - 0.25; });
    return alinhado ? caminhoForma('retangulo', x0, y0, w, h) : caminho(cantos.slice(0, 4), true);
  }
  if (nCantos >= 8 && nCantos <= 12) return caminhoForma('estrela', x0, y0, w, h);
  if (desvio / media < 0.2) return caminhoForma('circulo', x0, y0, w, h);
  return null;
}
